import React, { useEffect, useState } from "react";
import Sidebar from "../Sidebar/Sidebar";
import Navbar from "./Navbar";
import Header from "./header";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import ApiConfig from "../../common/config/ApiConfig";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { useLanguage } from "../../common/i18n/LanguageContext";

function LottoType() {
  const { t } = useLanguage();
  const pos = localStorage.getItem("position");
  const location = useLocation();
  // Deep-link support from LottoService.jsx's "จัดการกลุ่มรางวัล" button —
  // preselects the group filter to that lotto type instead of the first one.
  const lottoTypeIdParam = new URLSearchParams(location.search).get(
    "lottoTypeId",
  );
  // Always unfiltered — the "กรองตามประเภทหวย" dropdown filters this
  // client-side (see `groups` below), so the picker can still see every
  // group's rewardTypes (for cross-group exclusivity) regardless of which
  // lotto type is currently selected in the filter.
  const [allGroups, setAllGroups] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [lottoTypes, setLottoTypes] = useState([]);
  const [openAddPanel, setOpenAddPanel] = useState(null);
  const [selectedToAdd, setSelectedToAdd] = useState([]);
  const [catalogFilter, setCatalogFilter] = useState("");
  const [groupLottoTypeFilter, setGroupLottoTypeFilter] = useState("");

  const fetchGroups = () => {
    ApiConfig.get("/getLottoRewardTypeGroups").then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      setAllGroups(Array.isArray(result.data) ? result.data : []);
    });
  };

  const groups = groupLottoTypeFilter
    ? allGroups.filter(
        (g) => String(g.lottoType?.id) === String(groupLottoTypeFilter),
      )
    : allGroups;

  const fetchCatalog = () => {
    ApiConfig.get("/getLottoRewardTypes").then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      setCatalog(Array.isArray(result.data) ? result.data : []);
    });
  };

  const fetchLottoTypes = () => {
    ApiConfig.get("/getLottoTypes", {}).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      const list = Array.isArray(result.data) ? result.data : [];
      setLottoTypes(list);
      const matchesParam = list.some(
        (lt) => String(lt.id) === String(lottoTypeIdParam),
      );
      setGroupLottoTypeFilter(matchesParam ? lottoTypeIdParam : list[0]?.id);
    });
  };

  useEffect(() => {
    fetchGroups();
    fetchCatalog();
    fetchLottoTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const escapeHtml = (str) =>
    String(str)
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  const lottoTypeOptionsHtml = (selectedId) =>
    lottoTypes
      .map(
        (lt) =>
          `<option value="${lt.id}" ${
            lt.id === selectedId ? "selected" : ""
          }>${escapeHtml(lt.name)} (${escapeHtml(lt.type)})</option>`,
      )
      .join("");

  const handleCreateGroup = () => {
    if (!groupLottoTypeFilter) {
      toast.error(t("lotto_type_select_filter_first"), {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    Swal.fire({
      title: t("lotto_type_create_group_title"),
      input: "text",
      inputPlaceholder: t("lotto_type_group_name_label"),
      showCancelButton: true,
      confirmButtonText: t("common_create"),
      cancelButtonText: t("common_cancel"),
      inputValidator: (value) =>
        !value.trim() ? t("lotto_type_group_name_required") : null,
    }).then((result) => {
      if (!result.isConfirmed) return;

      ApiConfig.post("/createLottoRewardTypeGroup", {
        name: result.value.trim(),
        lottoTypeId: Number(groupLottoTypeFilter),
      }).then((res) => {
        if (res.resCode !== 200) {
          toast.error(res.message || t("lotto_type_create_group_error"), {
            position: "top-right",
            autoClose: 3000,
          });
          return;
        }
        toast.success(t("lotto_type_create_group_success"), {
          autoClose: 2000,
        });
        fetchGroups();
      });
    });
  };

  const handleEditGroup = (group) => {
    Swal.fire({
      title: t("lotto_type_edit_group_title"),
      html: `
        <input id="swal-name" class="swal2-input" placeholder="${t("lotto_type_group_name_label")}" value="${escapeHtml(
          group.name,
        )}" />
        <select id="swal-lottotype" class="swal2-select">
          ${lottoTypeOptionsHtml(group.lottoType?.id)}
        </select>
      `,
      showCancelButton: true,
      confirmButtonText: t("membertable_save"),
      cancelButtonText: t("common_cancel"),
      preConfirm: () => {
        const name = document.getElementById("swal-name").value.trim();
        const lottoTypeId = document.getElementById("swal-lottotype").value;
        if (!name) {
          Swal.showValidationMessage(t("lotto_type_group_name_required"));
          return false;
        }
        if (!lottoTypeId) {
          Swal.showValidationMessage(t("lotto_type_select_type_required"));
          return false;
        }
        return { name, lottoTypeId: Number(lottoTypeId) };
      },
    }).then((result) => {
      if (!result.isConfirmed) return;

      ApiConfig.post("/updateLottoRewardTypeGroup", {
        id: group.id,
        ...result.value,
      }).then((res) => {
        if (res.resCode !== 200) {
          toast.error(res.message || t("lotto_type_edit_group_error"), {
            position: "top-right",
            autoClose: 3000,
          });
          return;
        }
        toast.success(t("lotto_type_edit_group_success"), {
          autoClose: 2000,
        });
        fetchGroups();
      });
    });
  };

  const handleDeleteGroup = (group) => {
    Swal.fire({
      title: t("common_are_you_sure"),
      text: `${t("lotto_type_delete_group_confirm_prefix")} "${group.name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("lotto_type_confirm_yes_delete"),
      cancelButtonText: t("common_cancel"),
    }).then((result) => {
      if (!result.isConfirmed) return;

      ApiConfig.post("/deleteLottoRewardTypeGroup", { id: group.id }).then(
        (res) => {
          if (res.resCode !== 200) {
            toast.error(res.message || t("lotto_type_delete_group_error"), {
              position: "top-right",
              autoClose: 3000,
            });
            return;
          }
          toast.success(t("lotto_type_delete_group_success"), {
            autoClose: 2000,
          });
          fetchGroups();
        },
      );
    });
  };

  const handleRemoveRewardType = (group, rewardTypeId) => {
    ApiConfig.post("/removeLottoRewardTypesFromGroup", {
      groupId: group.id,
      rewardTypeIds: [rewardTypeId],
    }).then((res) => {
      if (res.resCode !== 200) {
        toast.error(res.message || t("lotto_type_remove_item_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      fetchGroups();
    });
  };

  const toggleAddPanel = (group) => {
    const isOpen = openAddPanel === group.id;
    setOpenAddPanel(isOpen ? null : group.id);
    setSelectedToAdd(
      isOpen ? [] : (group.rewardTypes || []).map((rt) => rt.id),
    );
    setCatalogFilter("");
  };

  const toggleSelectedToAdd = (id) => {
    setSelectedToAdd((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleAddRewardTypes = (group) => {
    const attachedIds = new Set((group.rewardTypes || []).map((rt) => rt.id));
    const newIds = selectedToAdd.filter((id) => !attachedIds.has(id));
    if (!newIds.length) return;

    ApiConfig.post("/addLottoRewardTypesToGroup", {
      groupId: group.id,
      rewardTypeIds: newIds,
    }).then((res) => {
      if (res.resCode !== 200) {
        toast.error(res.message || t("lotto_type_add_item_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      toast.success(t("lotto_type_add_item_success"), { autoClose: 2000 });
      toggleAddPanel(group);
      fetchGroups();
    });
  };

  // Which groups (plural — a reward type is many-to-many with groups, so the
  // same catalog row can legitimately belong to several groups across
  // different lotto types, e.g. every lotto type's own "4 ตัว" group reuses
  // the same global "4 ตัวบน"/"4 ตัวโดด" rows) already own each reward type,
  // across every group regardless of the active lotto-type filter. The
  // picker below only treats it as "used elsewhere" when one of the owning
  // groups shares the same lotto type as the one being edited — a reward
  // type can be reused across different lotto types, just not within the
  // same one.
  const rewardTypeOwnerMap = new Map();
  allGroups.forEach((g) => {
    (g.rewardTypes || []).forEach((rt) => {
      const owners = rewardTypeOwnerMap.get(rt.id) || [];
      owners.push(g);
      rewardTypeOwnerMap.set(rt.id, owners);
    });
  });

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <div className="shrink-0">
        <Navbar />
      </div>
      <div className="flex flex-1 overflow-hidden items-start">
        <div className="shrink-0 overflow-y-auto h-full">
          <Sidebar pos={pos} />
        </div>
        <div className="flex flex-col flex-1 max-h-full overflow-hidden">
          <div className="shrink-0">
            <Header title={t("menu_lotto_type_setting")} />
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            <div className="flex items-center justify-between gap-2 bg-white p-2 rounded-lg shadow-sm">
              <button
                onClick={handleCreateGroup}
                className="flex items-center gap-1 bg-gradient-to-b from-brand-gold-300 via-brand-gold-400 to-brand-gold-600 text-brand-ink-onGold border border-brand-gold-200 shadow-gold hover:brightness-105 text-sm font-bold px-3 py-1 rounded"
              >
                <AddIcon style={{ fontSize: 18 }} />
                {t("lotto_type_create_group_button")}
              </button>
              <select
                value={groupLottoTypeFilter}
                onChange={(e) => setGroupLottoTypeFilter(e.target.value)}
                className="text-sm border border-slate-300 rounded px-2 py-1"
              >
                {lottoTypes.map((lt) => (
                  <option key={lt.id} value={lt.id}>
                    {lt.name} ({lt.type})
                  </option>
                ))}
              </select>
            </div>

            {groups.length === 0 && (
              <div className="bg-white p-4 rounded-lg shadow-sm text-center text-slate-500">
                {t("lotto_type_no_groups_yet")}
              </div>
            )}

            {groups.map((group) => {
              const attachedIds = new Set(
                (group.rewardTypes || []).map((rt) => rt.id),
              );
              const available = catalog.filter(
                (rt) =>
                  !catalogFilter ||
                  rt.name.includes(catalogFilter) ||
                  rt.code.includes(catalogFilter),
              );

              return (
                <div
                  key={group.id}
                  className="bg-white p-3 rounded-lg shadow-sm space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-[15px]">
                        {group.name}
                      </span>
                      {group.lottoType && (
                        <span className="text-[11px] bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
                          {group.lottoType.name} ({group.lottoType.type})
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleAddPanel(group)}
                        className="flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white text-[12px] font-bold px-2 py-1 rounded"
                      >
                        <AddIcon style={{ fontSize: 16 }} />
                        {t("lotto_type_add_item_button")}
                      </button>
                      <button
                        onClick={() => handleEditGroup(group)}
                        className="flex items-center gap-1 bg-amber-500 hover:bg-amber-600 text-white text-[12px] font-bold px-2 py-1 rounded"
                      >
                        <EditIcon style={{ fontSize: 16 }} />
                        {t("common_edit")}
                      </button>
                      <button
                        onClick={() => handleDeleteGroup(group)}
                        className="flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white text-[12px] font-bold px-2 py-1 rounded"
                      >
                        <DeleteIcon style={{ fontSize: 16 }} />
                        {t("lotto_type_delete_group_button")}
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1">
                    {(group.rewardTypes || []).length === 0 && (
                      <span className="text-[12px] text-slate-400">
                        {t("lotto_type_no_items_in_group")}
                      </span>
                    )}
                    {(group.rewardTypes || []).map((rt) => (
                      <span
                        key={rt.id}
                        className="flex items-center gap-1 bg-sky-100 text-sky-800 text-[12px] font-bold px-2 py-1 rounded-full"
                        title={`${rt.code} (${rt.stake}) — ${rt.exp}`}
                      >
                        {rt.name}
                        <button
                          onClick={() => handleRemoveRewardType(group, rt.id)}
                          className="hover:text-red-600"
                        >
                          <CloseIcon style={{ fontSize: 14 }} />
                        </button>
                      </span>
                    ))}
                  </div>

                  {openAddPanel === group.id && (
                    <div className="border-t border-slate-200 pt-2 space-y-2">
                      <input
                        type="text"
                        value={catalogFilter}
                        onChange={(e) => setCatalogFilter(e.target.value)}
                        placeholder={t("lotto_type_search_placeholder")}
                        className="text-sm border border-slate-300 rounded px-2 py-1 w-full"
                      />
                      <div className="max-h-48 overflow-y-auto grid grid-cols-2 md:grid-cols-3 gap-1">
                        {available.length === 0 && (
                          <span className="text-[12px] text-slate-400 col-span-full">
                            {t("lotto_type_no_items_available")}
                          </span>
                        )}
                        {available.map((rt) => {
                          const isAttached = attachedIds.has(rt.id);
                          const owners = rewardTypeOwnerMap.get(rt.id) || [];
                          const ownerGroup = owners.find(
                            (g) =>
                              g.id !== group.id &&
                              g.lottoType?.id === group.lottoType?.id,
                          );
                          const isUsedElsewhere = !isAttached && !!ownerGroup;
                          const disabled = isAttached || isUsedElsewhere;
                          const title = isUsedElsewhere
                            ? `${rt.code} — ${t(
                                "lotto_type_used_in_group_prefix",
                              )} "${ownerGroup.name}"`
                            : `${rt.code} (${rt.stake}) — ${rt.exp}`;
                          return (
                            <label
                              key={rt.id}
                              className={`flex items-center gap-1 text-[12px] ${
                                disabled ? "text-slate-400" : "cursor-pointer"
                              }`}
                              title={title}
                            >
                              <input
                                type="checkbox"
                                checked={selectedToAdd.includes(rt.id)}
                                disabled={disabled}
                                onChange={() => toggleSelectedToAdd(rt.id)}
                              />
                              {rt.name}
                            </label>
                          );
                        })}
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => toggleAddPanel(group)}
                          className="text-[12px] font-bold px-3 py-1 rounded border border-slate-300"
                        >
                          {t("common_cancel")}
                        </button>
                        <button
                          onClick={() => handleAddRewardTypes(group)}
                          disabled={!selectedToAdd.length}
                          className="text-[12px] font-bold px-3 py-1 rounded bg-brand-blue-600 hover:bg-brand-blue-700 text-white disabled:opacity-50"
                        >
                          {t("lotto_type_add_count_button")} (
                          {selectedToAdd.length})
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LottoType;
